/**
 * @file crm-deals-query.ts
 * @description Состояние реестра сделок в адресе страницы (phase19.31, зона
 * «Клиенты и сделки», макет (в)): поиск по названию, воронка, «только
 * открытые», страница и её размер.
 *
 * «Только открытые» включено по умолчанию (как было в реестре до перевода):
 * в адрес пишется только выключенное состояние (`open=0`). Фильтр делает
 * сервер (`openOnly` → `closedAt = null`), кабинет его не подменяет.
 */

import type { listDeals } from "./api-crm";
import {
  CRM_DEFAULT_PAGE_SIZE,
  pageSizeFromParam,
  pageToOffset,
  positiveInt,
} from "./crm-list-query";

export interface DealsRegistryQuery {
  /** Название сделки (или его часть). */
  search: string;
  /** Воронка; пусто — все воронки. */
  pipelineId: string;
  openOnly: boolean;
  /** Номер страницы с единицы. */
  page: number;
  pageSize: number;
}

/** Имена параметров адреса — реестром, а не строками по коду. */
const KEY = { search: "q", pipelineId: "pipeline", openOnly: "open", page: "page", pageSize: "size" } as const;

/** Адрес → состояние. Неизвестные значения тихо заменяются умолчаниями. */
export function parseDealsQuery(params: URLSearchParams): DealsRegistryQuery {
  return {
    search: (params.get(KEY.search) ?? "").trim(),
    pipelineId: params.get(KEY.pipelineId) ?? "",
    openOnly: params.get(KEY.openOnly) !== "0",
    page: positiveInt(params.get(KEY.page), 1),
    pageSize: pageSizeFromParam(params.get(KEY.pageSize)),
  };
}

/** Состояние → адрес; значения по умолчанию не пишем. */
export function writeDealsQuery(query: DealsRegistryQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.search) params.set(KEY.search, query.search);
  if (query.pipelineId) params.set(KEY.pipelineId, query.pipelineId);
  if (!query.openOnly) params.set(KEY.openOnly, "0");
  if (query.page > 1) params.set(KEY.page, String(query.page));
  if (query.pageSize !== CRM_DEFAULT_PAGE_SIZE) params.set(KEY.pageSize, String(query.pageSize));
  return params;
}

/** Параметры запроса к серверу. */
export function toListDealsParams(query: DealsRegistryQuery): NonNullable<Parameters<typeof listDeals>[0]> {
  return {
    search: query.search || undefined,
    pipelineId: query.pipelineId || undefined,
    openOnly: query.openOnly || undefined,
    ...pageToOffset(query.page, query.pageSize),
  };
}

/** Сколько фильтров спрятано в шторку на телефоне: воронка и «только открытые». */
export function dealsSheetFilterCount(query: DealsRegistryQuery): number {
  return [query.pipelineId !== "", query.openOnly].filter(Boolean).length;
}

/** Список сужен фильтром — от этого зависит текст пустого списка. */
export function dealsQueryFiltered(query: DealsRegistryQuery): boolean {
  return query.search !== "" || dealsSheetFilterCount(query) > 0;
}

/** «Сбросить фильтры»: все сделки, размер страницы сохраняется. */
export function resetDealsQuery(query: DealsRegistryQuery): DealsRegistryQuery {
  return { search: "", pipelineId: "", openOnly: false, page: 1, pageSize: query.pageSize };
}
